import { useCallback, useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/browser';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { styled } from '@mui/material';
import { DisplayInlineError } from '../../../../common/ErrorHandling';
import { Form } from '@calefy-inc/informedMaterial';
import type { FormState } from 'informed';
import { useFormApi, useFormState } from 'informed';
import {
  GET_MANAGEMENT_WHITE_LABEL_SETTINGS,
  SAVE_WHITE_LABEL_LOGOS,
  UPDATE_WHITE_LABEL_SETTINGS,
} from '../../../../../queries/whiteLabelSettings';
import {
  buildInitialValues,
  buildMutationVariables,
  cloneFormResetBaseline,
  normalizeGraphqlStringList,
} from './whiteLabelSettingsFormHelpers';
import { CompanyInfoSection } from './sections/CompanyInfoSection';
import { WhiteLabelLogoChangeAction } from '../../../../../gql/graphql';
import type {
  WhiteLabelLogoChangeInput,
  WhiteLabelLogoField,
} from '../../../../../gql/graphql';
import { LogoSection, WHITE_LABEL_LOGO_SLOTS } from './sections/LogoSection';
import type { PendingLogoChange } from './sections/LogoSection';
import { PaletteSection } from './sections/PaletteSection';
import { TypographySection } from './sections/TypographySection';
import { InsuranceTypesSection } from './sections/InsuranceTypesSection';
import { CustomButtonsSection } from './sections/CustomButtonsSection';
import { FlagsSection } from './sections/FlagsSection';
import { PolicyStreamIntroductoryTextSection } from './sections/PolicyStreamIntroductoryTextSection';
import { useIsAdmin } from '../../../../../hooks';
import { errorify } from '../../../../../util';

function graphqlErrorsMessage(errors: unknown): string | null {
  if (!Array.isArray(errors) || errors.length === 0) return null;
  return errors
    .map((err) => {
      if (err && typeof err === 'object' && 'message' in err) {
        return String(Reflect.get(err, 'message'));
      }
      return errorify(err).message;
    })
    .filter((message) => message.length > 0)
    .join('\n');
}

/** Submit + reset — must render inside `<Form>` (Informed context). */
function SaveSubmitBar({
  disabled,
  resetBaseline,
  onAfterReset,
  hasPendingLogoDrafts,
}: {
  disabled: boolean;
  /** Snapshot from `buildInitialValues` — restored on Reset (unsaved edits discarded). */
  resetBaseline: Record<string, unknown>;
  /** Clears deferred logo draft state (not tracked by Informed `dirty`). */
  onAfterReset?: () => void;
  /** Enables Reset when only logo slots changed without touching other fields. */
  hasPendingLogoDrafts: boolean;
}) {
  const formApi = useFormApi();
  const { dirty } = useFormState();

  const handleReset = useCallback(() => {
    formApi.reset({ values: cloneFormResetBaseline(resetBaseline) });
    onAfterReset?.();
  }, [formApi, resetBaseline, onAfterReset]);

  const resetDisabled = disabled || (!dirty && !hasPendingLogoDrafts);

  return (
    <SaveSubmitRow>
      <ResetButton
        type='button'
        variant='outlined'
        disabled={resetDisabled}
        onClick={handleReset}
      >
        Reset
      </ResetButton>
      <SubmitButton type='submit' variant='contained' disabled={disabled}>
        {disabled ? 'Saving…' : 'Save'}
      </SubmitButton>
    </SaveSubmitRow>
  );
}

export function CompanyAccountForm() {
  const { token } = useAuth();
  const isAdmin = useIsAdmin();
  // Hook can yield undefined while permissions load — mutation expects a strict boolean for admin-only fields.
  const isAdminUser = isAdmin === true;
  const { enqueueSnackbar } = useSnackbar();

  /** Deferred logo uploads/removals — persisted on main Save with `UPLOAD` / `REMOVE` mutations. */
  const [pendingLogoChanges, setPendingLogoChanges] = useState<
    Partial<Record<WhiteLabelLogoField, PendingLogoChange>>
  >({});
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [submitSaving, setSubmitSaving] = useState(false);

  const showSaveError = useCallback(
    (message: string) => {
      setSaveErrorMessage(message);
      enqueueSnackbar(message, { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const handleLogoPendingChange = useCallback(
    (field: WhiteLabelLogoField, change: PendingLogoChange | null) => {
      setPendingLogoChanges((prev) => {
        if (change == null) {
          const next: Partial<Record<WhiteLabelLogoField, PendingLogoChange>> =
            { ...prev };
          delete next[field];
          return next;
        }
        return { ...prev, [field]: change };
      });
    },
    [],
  );

  const handleLogoDraftsReset = useCallback(() => {
    setPendingLogoChanges({});
  }, []);

  // Management query: returns this org's own WL row, or null when none exists yet.
  const [
    loadManagementSettings,
    {
      data: managementData,
      error: managementError,
      loading: managementLoading,
      called: managementCalled,
    },
  ] = useLazyQuery(GET_MANAGEMENT_WHITE_LABEL_SETTINGS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!token) return;
    void loadManagementSettings();
  }, [loadManagementSettings, token]);

  const refreshManagementSettings = useCallback(() => {
    if (!token) return;
    void loadManagementSettings();
  }, [loadManagementSettings, token]);

  // Before the first lazy execution, Apollo reports loading=false — keep the form in a loading shell until then.
  const queryLoading = token
    ? !managementCalled || (managementLoading && managementData == null)
    : false;
  const [updateSettings] = useMutation(UPDATE_WHITE_LABEL_SETTINGS, {
    // errorPolicy: 'all' lets the submit handler show GraphQL errors inline.
    errorPolicy: 'all',
  });

  const [saveWhiteLabelLogos] = useMutation(SAVE_WHITE_LABEL_LOGOS, {
    errorPolicy: 'all',
  });

  const paletteColourRoles = normalizeGraphqlStringList(
    managementData?.whiteLabelPaletteColourRoles,
  );
  const colourShadeFields = normalizeGraphqlStringList(
    managementData?.whiteLabelColourShadeFields,
  );
  const typographyHeadingLevels = normalizeGraphqlStringList(
    managementData?.whiteLabelTypographyHeadingLevels,
  );

  const managementSettings = managementData?.publicWhiteLabelSettings;

  // Form `key` remount does not reset this state — clear logo drafts when the loaded WL row identity changes.
  useEffect(() => {
    setPendingLogoChanges({});
  }, [
    managementSettings?.id,
    managementSettings?.slug,
    managementSettings?.organization?.id,
    token,
  ]);

  // A non-null `id` confirms the org already has a persisted WL row.
  const hasPersistedWhiteLabelSettings = token
    ? Boolean(managementSettings?.id)
    : undefined;

  const effectiveSettings = managementSettings;

  const initialValues = buildInitialValues(
    effectiveSettings,
    paletteColourRoles,
    colourShadeFields,
    typographyHeadingLevels,
  );

  const originalSlugForMutation =
    hasPersistedWhiteLabelSettings === true
      ? effectiveSettings?.slug ?? ''
      : '';

  /** Non-admins cannot create the first WL row (slug/host are admin-only); they may only edit an existing own row. */
  const canPersistWhiteLabelSettings =
    isAdminUser || hasPersistedWhiteLabelSettings === true;

  const handleFormSubmit = async (formState: FormState) => {
    if (submitSaving) return;
    setSubmitSaving(true);
    setSaveErrorMessage(null);
    try {
      if (!token) {
        showSaveError('No auth token — please log in again');
        return;
      }
      if (!canPersistWhiteLabelSettings) {
        showSaveError(
          'Only an administrator can set up white-label settings for this organization.',
        );
        return;
      }
      const formSlugTrimmed =
        formState.values.slug != null
          ? String(formState.values.slug).trim()
          : '';
      if (isAdmin === true && formSlugTrimmed.length === 0) {
        showSaveError('Slug is required — choose a unique slug before saving');
        return;
      }

      const updateResult = await updateSettings({
        variables: buildMutationVariables(
          { ...formState.values, originalSlug: originalSlugForMutation },
          token,
          isAdminUser,
          paletteColourRoles,
          colourShadeFields,
          typographyHeadingLevels,
        ),
      });

      // `FetchResult` typing may omit `errors`; read defensively for errorPolicy: 'all'.
      const updateErrors: unknown = Reflect.get(updateResult, 'errors');
      const updateErrorMessage = graphqlErrorsMessage(updateErrors);
      if (updateErrorMessage) {
        showSaveError(`Failed to save settings: ${updateErrorMessage}`);
        return;
      }

      const logoChanges: WhiteLabelLogoChangeInput[] = [];

      for (const slot of WHITE_LABEL_LOGO_SLOTS) {
        const change = pendingLogoChanges[slot.logoField];
        if (change == null) continue;

        if (change.type === 'upload') {
          logoChanges.push({
            action: WhiteLabelLogoChangeAction.Upload,
            logoField: slot.logoField,
            fileData: change.preview,
            filename: change.filename,
          });
        } else if (change.type === 'remove') {
          logoChanges.push({
            action: WhiteLabelLogoChangeAction.Remove,
            logoField: slot.logoField,
          });
        }
      }

      if (logoChanges.length > 0) {
        const wlId =
          effectiveSettings?.id == null ? NaN : Number(effectiveSettings.id);
        if (!Number.isInteger(wlId) || wlId <= 0) {
          showSaveError('Save settings once before uploading logos.');
          return;
        }

        const logoResult = await saveWhiteLabelLogos({
          variables: {
            token,
            whiteLabelSettingsId: wlId,
            changes: logoChanges,
          },
        });

        const logoErrors: unknown = Reflect.get(logoResult, 'errors');
        const logoErrorMessage = graphqlErrorsMessage(logoErrors);
        if (logoErrorMessage) {
          showSaveError(`Failed to save logos: ${logoErrorMessage}`);
          return;
        }

        if (logoResult.data?.saveWhiteLabelLogos?.success !== true) {
          showSaveError('Failed to save logos. Please try again.');
          return;
        }
      }

      setPendingLogoChanges({});
      setSaveErrorMessage(null);
      enqueueSnackbar('Settings saved successfully', {
        variant: 'success',
      });
      refreshManagementSettings();
    } catch (e) {
      const error = errorify(e);
      showSaveError(`Failed to save: ${error.message}`);
      Bugsnag.notify(error);
    } finally {
      setSubmitSaving(false);
    }
  };

  if (queryLoading) {
    return (
      <FormLoadingCenter>
        <CircularProgress />
      </FormLoadingCenter>
    );
  }

  if (managementError) {
    return (
      <FormErrorWrap>
        <DisplayInlineError
          error={managementError}
          processError={(e) => `Failed to load settings: ${e.message}`}
        />
      </FormErrorWrap>
    );
  }

  // Remount when server row / ownership changes so Informed picks up correct initialValues
  const formResetKey = [
    effectiveSettings?.id ?? 'none',
    effectiveSettings?.organization?.id ?? 'noorg',
    token ?? 'notoken',
    hasPersistedWhiteLabelSettings === true ? 'persisted' : 'bootstrap',
    effectiveSettings?.slug ?? '',
  ].join('-');

  const hasPendingLogoDrafts = Object.keys(pendingLogoChanges).length > 0;

  return (
    <Form
      key={formResetKey}
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
    >
      {/* Logos only after a persisted WL row exists (Save once with slug). */}
      <SettingsFormBody>
        {hasPersistedWhiteLabelSettings === true ? (
          <LogoSection
            settings={effectiveSettings}
            pendingChanges={pendingLogoChanges}
            onPendingChange={handleLogoPendingChange}
          />
        ) : null}
        <CompanyInfoSection />
        {isAdmin ? <PolicyStreamIntroductoryTextSection /> : null}
        <PaletteSection
          paletteColourRoles={paletteColourRoles}
          colourShadeFields={colourShadeFields}
        />
        {isAdmin ? (
          <TypographySection
            typographyHeadingLevels={typographyHeadingLevels}
          />
        ) : null}
        {isAdmin ? (
          <InsuranceTypesSection
            choices={managementData?.whiteLabelInsuranceTypeChoices ?? []}
          />
        ) : null}
        <CustomButtonsSection />
        {isAdmin ? (
          <FlagsSection
            definitions={
              managementData?.whiteLabelBehaviouralFlagDefinitions ?? []
            }
            whiteLabelInsuranceTypes={effectiveSettings?.types ?? []}
          />
        ) : null}

        {token && canPersistWhiteLabelSettings ? (
          <>
            {saveErrorMessage ? (
              <SaveErrorAlert severity='error'>
                {saveErrorMessage}
              </SaveErrorAlert>
            ) : null}
            <SaveSubmitBar
              disabled={submitSaving}
              resetBaseline={initialValues}
              onAfterReset={handleLogoDraftsReset}
              hasPendingLogoDrafts={hasPendingLogoDrafts}
            />
          </>
        ) : null}
      </SettingsFormBody>
    </Form>
  );
}

/** Centered spinner while settings query loads */
const FormLoadingCenter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(6, 0),
}));

/** Error state spacing under the page header */
const FormErrorWrap = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

/** Vertical rhythm for the whole settings form (sections keep their own margins) */
const SettingsFormBody = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

/** Save action aligned to the end of the form */
const SaveSubmitRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginTop: theme.spacing(3),
  gap: theme.spacing(2),
}));

const SaveErrorAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const ResetButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(14),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(14),
}));
